import { type FormikProps, withFormik } from "formik";
import { noop } from "lodash";
import { useEffect, useMemo } from "react";
import { connect, type ConnectedProps } from "react-redux";
import { type Dispatch } from "redux";
import EmbeddedVideoForm, { type EmbeddedVideoFormFields } from "./EmbeddedVideoForm";

import { RolePermissions } from "enums";
import { type RootState } from "features/Application/globaltypes/redux";
import Restricted from "features/Application/Restricted";
import { useEventCallback } from "hooks/useEventCallback";
import { bindAction, type WizardFormBase } from "interfaces";
import { embeddedVideoConfiguration } from "utils/validationSchemas/embeddedVideosValidationSchema";
import { type AutosaveProps, withAutosave } from "../../../../utils/withAutosave";
import { type EmbeddedVideoPayload } from "../services/types";
import { save } from "../state/EmbeddedVideoDetailsSlice";
import { resetState } from "../state/EmbeddedVideosEntityStateSlice";
import { embeddedVideoFormFieldsSelector } from "../state/selectors";
import { createEmbeddedVideo, updateEmbeddedVideo } from "../state/thunks/EmbeddedVideosEntityStateThunk";

export interface ConfigureProps extends WizardFormBase {
  entityId?: number;
}

export type ConfigurePropsAll = ConfigureProps & FormikProps<EmbeddedVideoFormFields> & PropsFromRedux & AutosaveProps;

export const Configure = (props: ConfigurePropsAll) => {
  const isEntityCreated = useMemo(() => props.id > 0 || props.isCreating, [props.id, props.isCreating]);

  const { isValid, onIsValidChange } = props;
  useEffect(() => {
    onIsValidChange?.(isValid);
  }, [isValid, onIsValidChange]);

  const saveEmbeddedVideo = useEventCallback(
    (paramValues?: EmbeddedVideoFormFields) => {
      props.saveEmbeddedVideo(paramValues || props.values);
    },
    [props],
  );

  const onNext = useEventCallback(() => {
    saveEmbeddedVideo();
    if (!isEntityCreated) {
      props.createEmbeddedVideo(mapFieldsToPayload(props.values));
    }
  }, [isEntityCreated, props, saveEmbeddedVideo]);

  useEffect(() => {
    props.acceptHandlers?.({
      onNext,
      onPrevious: saveEmbeddedVideo,
      onClose: props.resetEntityState,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Restricted
      permissions={[RolePermissions.AssetsCreate]}
      renderContent={(hasPermission) => (
        <EmbeddedVideoForm
          {...props}
          disablePreventTransitionPrompt={isEntityCreated}
          disabled={props.disabled || !hasPermission}
          isReadOnlyView={props.isPurchased}
        />
      )}
    />
  );
};

const mapFieldsToPayload = (embeddedVideoFields: EmbeddedVideoFormFields): EmbeddedVideoPayload => ({
  title: embeddedVideoFields.title.trim(),
  description: embeddedVideoFields.description.trim(),
  videoUrl: embeddedVideoFields.videoUrl.trim(),
  source: embeddedVideoFields.source,
  thumbnailPath: embeddedVideoFields.thumbnailUrl,
});

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const details = state.library.embeddedVideos.details;
  const entityState = state.library.embeddedVideos.entitState;
  return {
    id: entityState.entityId,
    isCreating: entityState.changingEntityState,
    hasBeenPublished: details.embeddedVideo.hasBeenPublished,
    isLoaded: details.isLoaded,
    isLoading: details.isLoading,
    isDraft: details.embeddedVideo.isDraft,
    isPurchased: details.embeddedVideo.isPurchased,
    formFields: embeddedVideoFormFieldsSelector(state),
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: Dispatch) => ({
  createEmbeddedVideo: bindAction(createEmbeddedVideo, dispatch),
  updateEmbeddedVideo: bindAction(updateEmbeddedVideo, dispatch),
  resetEntityState: bindAction(resetState, dispatch),
  saveEmbeddedVideo: bindAction(save, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

/* istanbul ignore next */
const withAutosaveComponent = withAutosave<ConfigurePropsAll, EmbeddedVideoFormFields, EmbeddedVideoPayload>({
  getInitValues: (props) => mapFieldsToPayload(props.formFields),
  stateProvider: mapFieldsToPayload,
  entityUpdater: (props) => props.updateEmbeddedVideo,
  isDeffered: (props) => !props.isLoaded,
})(Configure);

const component = withFormik({
  validationSchema: embeddedVideoConfiguration,
  enableReinitialize: true,
  mapPropsToValues: (props: ConfigureProps & PropsFromRedux): EmbeddedVideoFormFields => props.formFields,
  validateOnMount: true,
  handleSubmit: noop,
})(withAutosaveComponent);

const ConnectedComponent = connector(component);
export default ConnectedComponent;
